import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../SectionContainer';

import css from './SectionStepGrid.module.css';

/**
 * @typedef {Object} StepGridBlock
 * @property {string} blockId
 * @property {string} step the ordinal shown large and tinted, e.g. "01"
 * @property {Object} text the step description (Field data, e.g. paragraph)
 */

/**
 * Numbered "how it works" steps: a centered header above a row of cards, each showing a
 * large tinted ordinal and a short description.
 *
 * The ordinals alternate between the two brand colors (green, blue) at 30% opacity. That
 * alternation is done with :nth-child in CSS rather than a per-block color field, so the
 * rhythm keeps working no matter how many steps the content defines.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {string} props.sectionId
 * @param {'stepGrid'} props.sectionType
 * @param {Object} props.defaultClasses
 * @param {Object?} props.title header title (Field data)
 * @param {Object?} props.description header description (Field data)
 * @param {Array<StepGridBlock>} props.blocks
 * @param {Object?} props.appearance
 * @param {Object} props.options
 * @returns {JSX.Element}
 */
const SectionStepGrid = props => {
  const {
    sectionId,
    className,
    rootClassName,
    title,
    description,
    blocks = [],
    appearance,
    options,
  } = props;

  const fieldOptions = { fieldComponents: options?.fieldComponents };
  const hasHeader = hasDataInFields([title, description], fieldOptions);

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(rootClassName || css.root, className)}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeader ? (
        <header className={css.header}>
          <Field data={title} className={css.headerTitle} options={fieldOptions} />
          <Field data={description} className={css.headerDescription} options={fieldOptions} />
        </header>
      ) : null}

      <ol className={css.steps}>
        {blocks.map((block, index) => (
          <li key={block.blockId || `${sectionId}-step-${index}`} className={css.step}>
            <span className={css.stepNumber} aria-hidden="true">
              {block.step}
            </span>
            <Field data={block.text} className={css.stepText} options={fieldOptions} />
          </li>
        ))}
      </ol>
    </SectionContainer>
  );
};

export default SectionStepGrid;
